'use client';

import i18next from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next, useTranslation as useTranslationOrg } from 'react-i18next';

import {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  resolveStoredLanguage
} from './settings';
export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };

// English is bundled statically; de/fr/es are fetched on demand.
//
// I18nProvider is mounted in the ROOT layout, so everything this module imports ends
// up in the JS payload of EVERY route. The four locale files together are ~640 KB raw
// (~200 KB gzipped) and roughly 90% of that is dead weight for an English visitor who
// never touches the language switcher — it was ~44% of the homepage JS.
//
// `en` MUST stay a static import and MUST stay in the synchronous `resources` below.
// The root layout is statically prerendered with lng === DEFAULT_LANGUAGE, and i18next
// only finishes init synchronously when the initial language is already in the resource
// store (the backend connector short-circuits for bundles it already has). If `en` were
// fetched asynchronously the prerendered HTML would contain raw `pageContent.*` keys
// instead of copy, and I18nProvider's instantaneous changeLanguage() would break.
//
// The other three arrive via i18next-resources-to-backend. The template literal over a
// static directory is deliberate: it makes the bundler emit one lazily-loaded chunk per
// locale instead of inlining all of them into the shared bundle.
import en from './locales/en.json';

const resources = {
  en: { translation: en }
};

/**
 * IMPORTANT: To avoid hydration errors, we NEVER detect the language during SSR or
 * initial client render. Language is ALWAYS initialized as DEFAULT_LANGUAGE.
 * Any language switching happens purely client-side and after hydration: a returning
 * visitor's saved language is applied by restoreStoredLanguage(), which I18nProvider
 * runs once the page has hydrated. (There is deliberately no language-detector plugin
 * and no `detection` block here — nothing would read it, and it hid that nothing
 * restored the saved language at all.)
 */
i18next
  .use(resourcesToBackend((language: string) => import(`./locales/${language}.json`)))
  .use(initReactI18next)
  .init({
    resources,
    // Required whenever some languages are bundled and the rest come from a backend:
    // without it i18next sees `resources` and never consults the backend at all, so
    // de/fr/es would silently resolve to the English fallback forever.
    partialBundledLanguages: true,
    lng: DEFAULT_LANGUAGE, // Always use default language for initial render
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      escapeValue: false
    },
    load: 'languageOnly',
    saveMissing: false,
    // React i18next specific options
    react: {
      useSuspense: false, // Disable suspense - can cause issues with SSR
      bindI18n: 'languageChanged loaded',
      bindI18nStore: '',
      nsMode: 'default'
    }
  });

// The visitor's saved language, or null when there is none we can use.
export function readStoredLanguage(): string | null {
  try {
    return resolveStoredLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    // Storage blocked (privacy mode, sandboxed frame): behave like a first visit.
    return null;
  }
}

// Returning visitors: fetch their language's bundle now, in parallel with hydration, so the
// switch in restoreStoredLanguage() is instantaneous instead of waiting on the network. This
// only loads the bundle — i18next.language stays English, so hydration still matches the HTML.
if (typeof window !== 'undefined') {
  const stored = readStoredLanguage();
  if (stored && stored !== DEFAULT_LANGUAGE) void i18next.loadLanguages(stored);
}

// Simple wrapper around useTranslation - don't change language here to avoid hydration issues
export function useTranslation(ns?: string | string[], options = {}) {
  return useTranslationOrg(ns, options);
}

// Function to change language and ensure it's saved correctly - only called client-side after hydration
export function changeLanguage(lng: string) {
  if (typeof window === 'undefined') return false;

  if (!SUPPORTED_LANGUAGES.includes(lng)) {
    console.warn(`Attempted to switch to unsupported language: ${lng}`);
    return false;
  }

  i18next.changeLanguage(lng);
  document.documentElement.lang = lng;

  // Persisting is best effort and each store fails on its own (blocked storage throws,
  // sandboxed frames refuse cookies): a failure must not undo a switch that already happened.
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  } catch {}
  try {
    // Set the cookie so the middleware can pick it up for SSR
    document.cookie = `${LANGUAGE_STORAGE_KEY}=${lng}; path=/; max-age=31536000; samesite=lax`;
  } catch {}

  return true;
}

// How long to wait for the main thread to go idle before switching anyway, and the delay used
// where requestIdleCallback does not exist (Safari).
const RESTORE_IDLE_TIMEOUT_MS = 1000;
const RESTORE_FALLBACK_DELAY_MS = 50;

/**
 * Applies the language the visitor last picked. The server HTML — and so the hydration pass —
 * is always English, so this must only run once hydration is over; I18nProvider calls it from
 * an effect. It then waits for an idle moment rather than switching inside the effect itself:
 * Suspense boundaries that React hydrates lazily (the dashboard's loading.tsx) read
 * i18next.language when they render, and switching first would hand them French against
 * English markup. Returns a cancel function for the effect cleanup.
 */
export function restoreStoredLanguage(): () => void {
  // Re-read at fire time: the visitor may have used the switcher while we waited, and that
  // choice (it rewrites storage) must not be overwritten by the older one.
  const pending = () => {
    const stored = readStoredLanguage();
    return stored && stored !== i18next.language ? stored : null;
  };
  const apply = () => {
    const language = pending();
    if (language) changeLanguage(language);
  };

  if (!pending()) return () => {};

  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(apply, { timeout: RESTORE_IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(handle);
  }
  const timer = setTimeout(apply, RESTORE_FALLBACK_DELAY_MS);
  return () => clearTimeout(timer);
}

export default i18next;
